import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { ManufacturersService } from '../manufacturers.service'
import { ManufacturersRepository } from '../manufacturers.repository'
import { Manufacturer } from '../models/manufacturer.entity'

interface ManufacturerDetailState {
  manufacturer?: Manufacturer
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const MANUFACTURER_DETAIL_INITIAL_STATE: ManufacturerDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class ManufacturerDetailStore {
  constructor(
    private manufacturersService: ManufacturersService,
    private manufacturersRepository: ManufacturersRepository,
  ) {}

  private state = signal<ManufacturerDetailState>(
    MANUFACTURER_DETAIL_INITIAL_STATE,
  )

  // Selectors
  readonly manufacturer = computed(() => this.state().manufacturer)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(manufacturerId?: string): Promise<void> {
    if (!manufacturerId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.manufacturersService.read$(manufacturerId).subscribe({
        next: async (data) => {
          resolve()
          this.setData(
            data,
            this.state().manufacturer?._id
              ? this.state().manufacturer?._id !== data._id
              : false,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(manufacturer: Manufacturer, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.manufacturersService.upsert$(manufacturer).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          if (notify === true) {
            this.manufacturersRepository.notify({ code: 'MANUFACTURER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(manufacturer: Manufacturer, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      manufacturer,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
